import { inject, Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { catchError, concatMap, exhaustMap, finalize, Observable, of, switchMap, tap } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { NotificationMessageLevel, NotificationService } from '@lib/core';
import { CatalogType } from '@lib/catalog/product-tree-filter/ui';
import { IFileUploadMaxSize } from '@lib/upload-zone/ui';
import { SupplierBasketService } from '../service/supplier-basket.service';
import {
    AddAttachmentsRequest,
    Basket,
    BasketItem,
    CompanyInformation,
    CompanySetting,
    SupplierBasket,
    SupplierNote,
    TotalBasket,
} from '@lib/basket/suppliers-basket/basket-supplier/basket-supplier-item/ui';
import { BasketDialogService } from '@lib/basket/suppliers-basket/basket-supplier/data-access';
import {
    BasketItemRefreshDataService
} from '@lib/basket/suppliers-basket/basket-supplier/basket-supplier-item/data-access';

export interface ExistSupplierWarehouse {
    existInSupplierWarehouse: boolean;
    id: number;
}

export interface SupplierWarehouseQuantity {
    quantity: number;
    id: number;
}

export interface SupplierContactPerson {
    companiesInfoData: CompanyInformation[];
    supplierBasketId: string;
}

export interface SuppliersBasketState {
    supplierBaskets: SupplierBasket[];
    currencyTotals: TotalBasket[];
    companySetting: CompanySetting | null;
    uploadMaxSize: IFileUploadMaxSize | null;
    isLoading?: boolean;
}

export const DEFAULT_SUPPLIERS_BASKET_STATE = (): SuppliersBasketState => ({
    supplierBaskets: [],
    currencyTotals: [],
    companySetting: null,
    uploadMaxSize: null,
    isLoading: false,
});
// TODO MOVE THIS TO STORE
@Injectable()
export class SuppliersBasketStore extends ComponentStore<SuppliersBasketState> {
    private readonly notificationService = inject(NotificationService);

    suppliersBasketService = inject(SupplierBasketService);
    basketDialogService = inject(BasketDialogService);
    basketItemRefreshDataService = inject(BasketItemRefreshDataService);

    constructor() {
        super(DEFAULT_SUPPLIERS_BASKET_STATE());

        this.basketItemRefreshDataService.updateItemAmountSubject.subscribe((basketItemUpdatedRefreshData) => {
            this.addStateFromBasket(basketItemUpdatedRefreshData.basket);
        });

        this.basketItemRefreshDataService.deleteItemSubject.subscribe((basket) => {
            this.addStateFromBasket(basket);
        });

        this.basketItemRefreshDataService.deliveryCostChanged.subscribe((basket) => {
            this.addStateFromBasket(basket);
        });
    }

    readonly supplierBaskets = this.select((state) => {
        return state.supplierBaskets;
    });

    readonly currencyTotals = this.select((state) => {
        return state.currencyTotals;
    });

    readonly companySetting = this.select((state) => {
        return state.companySetting;
    });

    addStateFromBasket = this.updater((state: SuppliersBasketState, basket: Basket | null) => {
        const newBasket = basket ? { ...basket } : null;
        return {
            ...state,
            supplierBaskets: newBasket?.supplierBaskets || [],
            currencyTotals: newBasket?.currencyTotals || [],
        };
    });

    addCurrencyTotals = this.updater((state: SuppliersBasketState, basket: Basket | null) => {
        return {
            ...state,
            currencyTotals: basket?.currencyTotals || [],
        };
    });

    setIsLoading = this.updater((state: SuppliersBasketState, isLoading: boolean) => {
        return {
            ...state,
            isLoading,
        };
    });

    addSupplierNotes = this.updater((state: SuppliersBasketState, supplierNote: SupplierNote) => {
        const supplierBasket = state.supplierBaskets.find(
            (supplierBasket) => supplierBasket.supplier.id === supplierNote.supplierId,
        );

        const updatedSupplierBasket = { ...supplierBasket, supplierNote: supplierNote };

        return {
            ...state,
            supplierBaskets: [
                ...state.supplierBaskets.map((supplierBasket) =>
                    supplierBasket.supplier.id === supplierNote.supplierId ? updatedSupplierBasket : supplierBasket,
                ),
            ],
        };
    });

    setCompanySetting = this.updater((state: SuppliersBasketState, companySetting: CompanySetting) => {
        return {
            ...state,
            companySetting,
        };
    });

    setIfExistInSupplierWarehouse = this.updater(
        (state: SuppliersBasketState, existSupplierWarehouse: ExistSupplierWarehouse) => {
            const supplierBasketsNew = [...state.supplierBaskets];
            supplierBasketsNew.forEach((supplierBasket) =>
                supplierBasket.basketItems.forEach((elem) => {
                    if (elem.id === existSupplierWarehouse.id) {
                        elem.existInSupplierWarehouse = existSupplierWarehouse.existInSupplierWarehouse;
                    }
                }),
            );
            return {
                ...state,
                supplierBaskets: [...supplierBasketsNew],
            };
        },
    );

    addQuantityInStock = this.updater(
        (state: SuppliersBasketState, existSupplierWarehouseQuantity: SupplierWarehouseQuantity) => {
            const supplierBasketsNew = [...state.supplierBaskets];
            supplierBasketsNew.forEach((supplierBasket) =>
                supplierBasket.basketItems.forEach((elem) => {
                    if (elem.id === existSupplierWarehouseQuantity.id) {
                        elem.quantityInStock = existSupplierWarehouseQuantity.quantity;
                    }
                }),
            );
            return {
                ...state,
                supplierBaskets: [...supplierBasketsNew],
            };
        },
    );

    addContactPerson = this.updater((state: SuppliersBasketState, contactPersonData: SupplierContactPerson) => {
        const supplierBasketsNew = [...state.supplierBaskets];

        // TODO be careful about mutating the array
        supplierBasketsNew.forEach((supplierBasket) => {
            if (supplierBasket.supplier.id === contactPersonData.supplierBasketId) {
                supplierBasket.supplierContactsDropdown = contactPersonData.companiesInfoData;
            }
        });
        return {
            ...state,
            supplierBaskets: [...supplierBasketsNew],
        };
    });

    addUploadMaxSize = this.updater((state: SuppliersBasketState, uploadMaxSize: IFileUploadMaxSize) => {
        return {
            ...state,
            uploadMaxSize,
        };
    });

    //TODO Commented code will be deleted when we are finished with refactoring
    // openUploadFileBasketItemDialog$(item: BasketItemRequest) {
    //     this.openUploadFileBasketItemDialog(item);
    // }
    //
    // openProductDialog$(productId: string) {
    //     this.openProductDialog(productId);
    // }

    readonly loadBasket$ = this.effect((origin$) =>
        origin$.pipe(
            switchMap(() => {
                this.setIsLoading(true);
                return this.suppliersBasketService.getBasket().pipe(
                    tap((basket: Basket) => {
                        this.addStateFromBasket(basket);

                        // TODO check if this is needed or we need to call this from somewhere else
                        //  we are calling this all the time and in lot of effects, CHECK THIS
                        this.checkExistenceInWarehouse(basket.supplierBaskets);
                        this.getQuantityInStock(basket.supplierBaskets);

                        // TODO in the future when we move this part to a new state we will check what to do with this
                        //this.updateSuppliersContacts(basket.supplierBaskets);

                        // TODO check if this is needed or we need to call this from somewhere else
                        this.getCompanySetting$();

                        basket.supplierBaskets.forEach((supplierBasket) => {
                            this.getSupplierNote$(supplierBasket.supplier.id);
                        });
                    }),
                    catchError((error) => {
                        return of(error);
                    }),
                    finalize(() => {
                        this.setIsLoading(false);
                    }),
                );
            }),
        ),
    );

    readonly getSupplierNote$ = this.effect((origin$: Observable<string>) =>
        origin$.pipe(
            concatMap((supplierId) => {
                this.setIsLoading(true);
                return this.suppliersBasketService.getSupplierNote(supplierId).pipe(
                    tap((supplierNote: SupplierNote) => {
                        this.addSupplierNotes(supplierNote);
                    }),
                    catchError((error: HttpErrorResponse) => {
                        this.notificationService.showNotification(error.error.detail, NotificationMessageLevel.ERROR);
                        return of(error);
                    }),
                    finalize(() => {
                        this.setIsLoading(false);
                    }),
                );
            }),
        ),
    );

    readonly getCompanySetting$ = this.effect((origin$) =>
        origin$.pipe(
            concatMap(() => {
                this.setIsLoading(true);
                return this.suppliersBasketService.isSpecifyDeliveryDate().pipe(
                    tap((specifyDeliveryDate: CompanySetting) => {
                        this.setCompanySetting(specifyDeliveryDate);
                    }),
                    catchError((error) => {
                        return of(error);
                    }),
                    finalize(() => {
                        this.setIsLoading(false);
                    }),
                );
            }),
        ),
    );

    readonly existInSupplierWarehouse$ = this.effect((origin$: Observable<BasketItem>) =>
        origin$.pipe(
            concatMap((supplierBasket) => {
                this.setIsLoading(true);
                return this.suppliersBasketService.existInSupplierWarehouse(supplierBasket).pipe(
                    tap((supplierWarehouse) => {
                        const existSupplierWarehouse = {
                            existInSupplierWarehouse: supplierWarehouse,
                            id: supplierBasket.id,
                        } as ExistSupplierWarehouse;
                        this.setIfExistInSupplierWarehouse(existSupplierWarehouse);
                    }),
                    catchError((error) => {
                        return of(error);
                    }),
                    finalize(() => {
                        this.setIsLoading(false);
                    }),
                );
            }),
        ),
    );

    readonly addQuantityInStock$ = this.effect((origin$: Observable<BasketItem>) =>
        origin$.pipe(
            concatMap((basketItem) => {
                this.setIsLoading(true);
                return this.suppliersBasketService.getQuantityInStock(basketItem).pipe(
                    tap((quantity: any) => {
                        const supplierWarehouseQuantity = {
                            quantity: quantity?.quantity,
                            id: basketItem.id,
                        } as SupplierWarehouseQuantity;
                        this.addQuantityInStock(supplierWarehouseQuantity);
                    }),
                    catchError((error) => {
                        return of(error);
                    }),
                    finalize(() => {
                        this.setIsLoading(false);
                    }),
                );
            }),
        ),
    );

    readonly loadContactPerson$ = this.effect((origin$: Observable<string>) =>
        origin$.pipe(
            concatMap((supplierBasketId) => {
                return this.suppliersBasketService.getContactPerson(supplierBasketId).pipe(
                    tap((companiesInfo: CompanyInformation[]) => {
                        const companiesInfoData = {
                            companiesInfoData: companiesInfo,
                            supplierBasketId,
                        } as SupplierContactPerson;
                        this.addContactPerson(companiesInfoData);
                    }),
                    catchError((error) => {
                        return of(error);
                    }),
                    finalize(() => {
                        this.setIsLoading(false);
                    }),
                );
            }),
        ),
    );

    //TODO CHnECK THIS
    readonly loadBasketUpdateAttachment$ = this.effect((origin$) =>
        origin$.pipe(
            switchMap(() => {
                this.setIsLoading(true);
                return this.suppliersBasketService.getBasket().pipe(
                    tap((basket: Basket) => {
                        // TODO check if this is needed or we need to call this from somewhere else
                        //  we are calling this all the time and in lot of effects, CHECK THIS
                        this.checkExistenceInWarehouse(basket.supplierBaskets);
                        this.getQuantityInStock(basket.supplierBaskets);

                        // TODO in the future when we move this part to a new state we will check what to do with this
                        //this.updateSuppliersContacts(basket.supplierBaskets);

                        // TODO check if this is needed or we need to call this from somewhere else
                        this.getCompanySetting$();
                    }),
                    catchError((error) => {
                        return of(error);
                    }),
                    finalize(() => {
                        this.setIsLoading(false);
                    }),
                );
            }),
        ),
    );

    readonly addAttachments$ = this.effect((origin$: Observable<AddAttachmentsRequest>) =>
        origin$.pipe(
            exhaustMap((attachments) => {
                this.setIsLoading(true);
                return this.suppliersBasketService.addAttachments(attachments).pipe(
                    tap(() => {
                        this.loadBasketUpdateAttachment$();
                    }),
                    catchError((error) => {
                        return of(error);
                    }),
                    finalize(() => {
                        this.setIsLoading(false);
                    }),
                );
            }),
        ),
    );

    readonly getMaxUploadSize$ = this.effect((origin$) =>
        origin$.pipe(
            exhaustMap(() => {
                this.setIsLoading(true);
                return this.suppliersBasketService.getMaxUploadSize().pipe(
                    tap((maxSize) => {
                        this.addUploadMaxSize(maxSize);
                    }),
                    catchError((error) => {
                        return of(error);
                    }),
                    finalize(() => {
                        this.setIsLoading(false);
                    }),
                );
            }),
        ),
    );

    //TODO Commented code will be deleted when we are finished with refactoring
    // private openProductDialog(productId: string): void {
    //     this.productDialogService.openProductDialog(productId);
    // }
    //
    // private openUploadFileBasketItemDialog(item: BasketItemRequest): void {
    //     const maxFileSize = this.get().uploadMaxSize;
    //
    //     if (maxFileSize) {
    //         this.basketDialogService.openUploadZoneDialog(maxFileSize).subscribe((res) => {
    //             const addAttachmentsRequest = {
    //                 supplierBasketId: item.supplierBasketId,
    //                 item: item.basketItem,
    //                 attachments: res && res?.files,
    //             };
    //             if (res !== undefined) {
    //                 this.addBasketItemAttachments$(addAttachmentsRequest);
    //             }
    //         });
    //     }
    // }

    private openUploadFileDialog(supplierBasketId: string): void {
        const maxFileSize = this.get().uploadMaxSize;

        if (maxFileSize) {
            this.basketDialogService.openUploadZoneDialog(maxFileSize).subscribe((res) => {
                const addAttachmentsRequest = {
                    supplierBasketId: supplierBasketId,
                    attachments: res && res?.files,
                };
                if (res !== undefined) {
                    this.addAttachments$(addAttachmentsRequest);
                }
            });
        }
    }

    private checkExistenceInWarehouse(supplierBaskets: SupplierBasket[]) {
        supplierBaskets.forEach((el) => {
            el.basketItems.forEach((item) => {
                if (this.shouldCheckInWarehouse(item)) {
                    this.existInSupplierWarehouse$(item);
                }
            });
        });
    }

    private getQuantityInStock(supplierBaskets: SupplierBasket[]) {
        supplierBaskets.forEach((el) => {
            el.basketItems.forEach((item) => {
                if (this.shouldCheckInWarehouse(item)) {
                    this.addQuantityInStock$(item);
                }
            });
        });
    }

    private updateSuppliersContacts(supplierBaskets: SupplierBasket[]) {
        supplierBaskets.forEach((el) => {
            this.loadContactPerson$(el.supplier.id);
        });
    }

    private shouldCheckInWarehouse(item: BasketItem): boolean {
        return CatalogType.UNITE !== item?.product?.type && CatalogType.OCI !== item?.product?.type && CatalogType.FREIGHT_COST !== item?.product?.type && CatalogType.QUOTATION !== item?.product?.type;
    }
}
